/**
 * The dataset types the platform accepts. One place: the creation dialog, the list and the
 * detail page read the label, the glyph and what a training run consumes from the same entry.
 * `available: false` is a type the contract names but does not accept yet — it is shown and
 * cannot be chosen.
 *
 * What a type ACCEPTS is not written here. The server decides it and publishes it at
 * `GET /dataset-kinds`; `useDatasetKindCapabilities` records the answer below. A copy kept
 * here would be a second answer to the same question, and the two drifted: the dialog offered
 * `.txt` while the server refused it after the upload, and the server took `.pptx`, `.xlsx`
 * and `.html`, which nothing reads (W8-WALK).
 */
import { reactive } from 'vue'

import type { IconName } from '~/utils/icons'

export interface DatasetKind {
  id: string
  label: string
  icon: IconName
  /** What a training run reads from a version of this type. */
  consumes: string
  available: boolean
}

export const DATASET_KINDS: DatasetKind[] = [
  { id: 'documents', label: 'Documents', icon: 'kind-documents', consumes: 'Questions and answers written from the files', available: true },
  { id: 'tabular', label: 'Tabular', icon: 'kind-tabular', consumes: 'The validated table', available: true },
  { id: 'conversations', label: 'Conversations', icon: 'kind-conversations', consumes: 'The validated conversation file', available: true },
  { id: 'instructions', label: 'Instructions', icon: 'kind-instructions', consumes: 'Task and response pairs', available: false },
  { id: 'time_series', label: 'Time series', icon: 'kind-timeseries', consumes: 'Rows in time order', available: false },
  { id: 'image_classification', label: 'Image classification', icon: 'kind-image-class', consumes: 'Images with labels', available: false },
  { id: 'object_detection', label: 'Object detection', icon: 'kind-detection', consumes: 'Images with annotations', available: false },
  { id: 'image_generation', label: 'Image generation', icon: 'kind-imagegen', consumes: 'Images with captions', available: false },
  { id: 'audio', label: 'Audio', icon: 'kind-audio', consumes: 'Audio with transcriptions', available: false },
  { id: 'video', label: 'Video', icon: 'kind-video', consumes: 'Video with labels and subtitles', available: false },
  { id: 'code', label: 'Code', icon: 'kind-code', consumes: 'Source files', available: false },
  { id: 'recommendations', label: 'Recommendations', icon: 'kind-recommend', consumes: 'User and item events', available: false },
  { id: 'preferences', label: 'Preferences', icon: 'kind-preferences', consumes: 'Ranked response pairs', available: false },
  { id: 'search', label: 'Search', icon: 'kind-search-data', consumes: 'Queries, documents and relevance', available: false },
]

/**
 * What each kind accepts, as the server last answered. Empty until the answer arrives;
 * a caller that filters files waits for it rather than guessing, because guessing is the
 * second list this record exists to remove.
 */
const ACCEPTED: Record<string, string[]> = reactive({})

/** Records the server's answer. Called by `useDatasetKindCapabilities`, nowhere else. */
export function recordAcceptedExtensions(capabilities: Array<{ kind: string; accepts: string[] }>): void {
  for (const capability of capabilities) ACCEPTED[capability.kind] = [...capability.accepts]
}

/** The extensions this kind accepts; empty while the server has not answered yet. */
export const acceptsOf = (kind: DatasetKind): string[] => ACCEPTED[kind.id] ?? []

/** True once the server has said what any kind accepts. */
export const kindCapabilitiesKnown = (): boolean => Object.keys(ACCEPTED).length > 0

export const kindOf = (id?: string | null): DatasetKind | undefined => DATASET_KINDS.find((kind) => kind.id === id)
export const kindLabel = (id?: string | null): string => kindOf(id)?.label ?? '—'
export const kindIcon = (id?: string | null): IconName => kindOf(id)?.icon ?? 'datasets'
/** ".pdf" for "TCK-000318.PDF"; "" when the name carries no extension. */
export const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(dot).toLowerCase() : ''
}
export const acceptsFile = (kind: DatasetKind, name: string): boolean => acceptsOf(kind).includes(extensionOf(name))
/** Extensions as words for a hint line: "pdf · docx · md · txt". */
export const acceptedWords = (kind: DatasetKind): string => acceptsOf(kind).map((entry) => entry.slice(1)).join(' · ')
/** How many of these files an available type would take. */
export const matchCount = (kind: DatasetKind, names: string[]): number => names.filter((name) => acceptsFile(kind, name)).length
/** No extension of any type reads this file, so no type can take it. */
export const unknownFormat = (name: string): boolean => !DATASET_KINDS.some((kind) => acceptsFile(kind, name))
/**
 * The type the loaded files point at: the available type that takes the most of them.
 * A tie keeps the order above (documents before tabular before conversations).
 */
export function suggestKind(names: string[]): string | null {
  if (!names.length) return null
  let best: { id: string; count: number } | null = null
  for (const kind of DATASET_KINDS) {
    if (!kind.available) continue
    const count = matchCount(kind, names)
    if (count && (!best || count > best.count)) best = { id: kind.id, count }
  }
  return best?.id ?? null
}
